import Vue from 'vue';
import VueRouter, { NavigationGuardNext, Route } from 'vue-router';
import MainLayout from '@/layouts/main/index.vue';
import EmptyLayout from '@/layouts/empty/index.vue';
import GuestLayout from '@/layouts/guest/index.vue';
import { scheduleRoutes } from '@/router/routes/imports/schedule.routes';
import { hblRoutes } from '@/router/routes/imports/hbl.routes';
import { mblRoutes } from '@/router/routes/imports/mbl.routes';
import { exportsRoutes } from '@/router/routes/exports/exports.routes';
import i18n from '@/i18n';
import { ManifestType } from '@/types/manifest/manifest.enums';
import { profileRoutes } from '@/router/routes/profile.routes';
import { usePusher } from '@/composables/usePusher';
import { useUiStore } from '@/store/modules/useUiStore';
import { useUserStore } from '@/store/modules/user/useUserStore';
import pinia from '@/plugins/pinia';
import { Authentication } from '@/modules/authentication/Authentication';
import { applyTextTransformClassToBody } from '@/helpers/settings/textTransform';
import { customsRoutes } from '@/router/routes/customs/customs.routes';
import { ediRoutes } from '@/router/routes/edi.routes';
import { PermissionsAirImportsFeatures, PermissionsImportFeatures } from '@/constants/permissions';
import { reportsRoutes } from '@/router/routes/reports.routes';
import { loadsRoutes } from '@/router/routes/loads.routes';
import { mawbRoutes } from '@/router/routes/air-imports/mawb.routes';
import { photoRoutes } from '@/router/routes/photo.routes';
import { airHblRoutes } from './routes/air-imports/air-hbl.routes';
import { financeRoutes } from './routes/finance.routes';
import { inventoryLocationsRoutes } from './routes/inventory-locations.routes';
import { useOnlineStore } from '@/store/modules/useOnlineStore';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    component: MainLayout,
    redirect: {
      name: 'Home',
    },
    children: [
      ...scheduleRoutes,
      ...loadsRoutes,
      ...mblRoutes,
      ...hblRoutes,
      ...profileRoutes,
      ...exportsRoutes,
      ...customsRoutes,
      ...ediRoutes,
      ...reportsRoutes,
      ...mawbRoutes,
      ...airHblRoutes,
      ...photoRoutes,
      ...financeRoutes,
      ...inventoryLocationsRoutes,
      {
        name: 'Home',
        meta: {
          pageTitle: 'Home',
        },
        path: 'home',
        component: () => import('@/views/HomePage.vue'),
      },
      {
        name: 'ManifestImports',
        meta: {
          pageTitle: i18n.t('ManifestImports'),
          permission: PermissionsImportFeatures.MBL,
          breadCrumbItem(route: Route) {
            const manifestType = route.query.tab as ManifestType;
            return {
              text: `${manifestType ?? 'MBL'} List View`,
              to: {
                name: 'ManifestImports',
                query: route.query,
              },
            };
          },
        },
        path: 'imports/manifest',
        // component: () => import('@/views/imports/ManifestImportsView.vue'),
        component: () => import('@/views/imports/ManifestImportsViewVirtual.vue'),
      },
      {
        name: 'AirImports',
        path: '/air/imports',
        meta: {
          pageTitle: 'Air Imports',
          permission: PermissionsAirImportsFeatures.MAWB,
          breadCrumbItem(route: Route) {
            const manifestType = route.query.tab as ManifestType;
            return {
              text: `${manifestType ?? 'MAWB'} List View`,
              to: {
                name: 'AirImports',
                query: route.query,
              },
            };
          },
        },
        component: () => import('@/views/air-imports/AirImportsView.vue'),
      },
      {
        path: '/organization',
        meta: {
          pageTitle: 'Organization Profile',
        },
        component: () => import('@/views/organization/OrganizationView.vue'),
      },
    ],
  },
  {
    path: '/guest',
    name: 'Guest',
    component: GuestLayout,
    redirect: 'login',
    meta: { anonymousOnly: true },
    children: [
      {
        name: 'Login',
        path: 'login',
        meta: { anonymousOnly: true, pageTitle: 'Login' },
        component: () => import('../views/LoginView.vue'),
      },
    ],
  },
  {
    path: '/zebra-eb',
    component: () => import('@/features/zebra-eb/layouts/ZebraEbLayout.vue'),
    meta: { anonymousOnly: true, directAccess: true, pageTitle: 'Zebra Enterprise Browser' },
    children: [
      {
        name: 'ZebraEB',
        path: '/',
        meta: { anonymousOnly: true, directAccess: true, pageTitle: 'Zebra Enterprise Browser' },
        component: () => import('../features/zebra-eb/views/ZebraEbView.vue'),
      },
    ],
  },
  { path: '*', redirect: '/' },
];

const router = new VueRouter({
  mode: 'hash',
  routes,
});

const redirectToLogin = () => {
  const authUrlPath = '/login/#/login';
  window.location.href =
    window.location.origin +
    authUrlPath +
    '?redirect=' +
    window.location.origin +
    window.location.pathname;
};

const checkUserAuthenticated = async (to: Route, from: Route, next: NavigationGuardNext) => {
  const userStore = useUserStore(pinia);
  const { anonymousOnly = false, directAccess = false } = to.meta ?? {};

  if (!Authentication.accessToken && !anonymousOnly) {
    if (import.meta.env.PROD) {
      return redirectToLogin();
    }
    return next({ name: 'Login' });
  }

  if (!userStore.isAuthenticated && anonymousOnly) {
    // Allow direct access to the route bypassing all checks for special cases like zebra-eb feature
    if (directAccess) {
      return next();
    }
    if (import.meta.env.PROD) {
      return redirectToLogin();
    }
    return next();
  }

  if (userStore.isAuthenticated && anonymousOnly) {
    return next('/');
  }

  if (!userStore.isAuthenticated && !anonymousOnly) {
    const uiStore = useUiStore(pinia);
    uiStore.toggleLoader(true);
    try {
      await userStore.initializeUser();
    } catch {
      userStore.logoutUser();

      if (import.meta.env.PROD) {
        return redirectToLogin();
      }
      if (from.name !== 'Login') next({ name: 'Login' });
    } finally {
      uiStore.toggleLoader(false);
    }
  }
};

const checkUserPermissionToRoute = (permissionKey: string): boolean => {
  const userStore = useUserStore(pinia);

  if (!userStore.permissions?.groups) {
    return false;
  }

  for (const [groupKey, group] of Object.entries(userStore.permissions.groups)) {
    if (groupKey === permissionKey) {
      return !!group?.enabled;
    }
    if (!group?.features) {
      continue;
    }
    for (const [featureKey, feature] of Object.entries(group.features)) {
      if (featureKey === permissionKey) {
        return !!feature?.accessType.read;
      }
      if (!feature?.functions) {
        continue;
      }
      for (const [functionKey, functionValue] of Object.entries(feature.functions)) {
        if (functionKey !== permissionKey) {
          continue;
        }
        return !!functionValue?.accessType.read;
      }
    }
  }
  return false;
};

const handleAppTitle = (to: Route, from: Route) => {
  if (to.meta?.dynamicTitle && from.name === to.name) return;

  const title = ((to: Route): string => {
    if (to.params?.pageTitle) return to.params?.pageTitle;
    return to?.meta?.pageTitle || 'Logiware';
  })(to);

  document.title = title?.concat(' - CFS') || 'CFS - Logiware';
};

// every route is assumed to be for authorized users, unless it has meta.anonymousOnly
router.beforeEach(async (to, from, next) => {
  handleAppTitle(to, from);

  usePusher().ensureAppReload(to);

  await checkUserAuthenticated(to, from, next);

  if (to.meta?.permission && !checkUserPermissionToRoute(to.meta.permission)) {
    Vue.toasted.info("You don't have access to this page!");
    return next({ name: 'Home' });
  }

  if (
    typeof to.meta?.breadCrumbs === 'function' &&
    typeof from.meta?.breadCrumbItem === 'function'
  ) {
    to.meta!.fromBreadCrumb = from.meta?.breadCrumbItem(from);
  }

  useOnlineStore().updateCurrentPage(to.fullPath);

  next();
});

router.afterEach((to: Route) => {
  applyTextTransformClassToBody(to.name === 'Login' ? 'MIXED' : undefined);
});

export default router;
